import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Info, CheckCircle2, AlertTriangle, XCircle, X } from 'lucide-react';
import { cn } from '@/lib/utils';

/* ─── Alert Variants ───────────────────────────────────────────────────────── */

const alertVariants = cva(
  'relative flex gap-3 rounded-lg p-4 shadow-card text-sm',
  {
    variants: {
      variant: {
        default:
          'bg-raios-card border-l-[3px] border-l-raios-border text-raios-text',
        success:
          'bg-raios-success/5 border-l-[3px] border-l-raios-success text-raios-text',
        warning:
          'bg-raios-warning/5 border-l-[3px] border-l-raios-warning text-raios-text',
        destructive:
          'bg-raios-danger/5 border-l-[3px] border-l-raios-danger text-raios-text',
        info:
          'bg-raios-info/5 border-l-[3px] border-l-raios-info text-raios-text',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const variantIconMap = {
  default: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  destructive: XCircle,
  info: Info,
} as const;

const variantIconColorMap = {
  default: 'text-raios-muted',
  success: 'text-raios-success-text',
  warning: 'text-raios-warning-text',
  destructive: 'text-raios-danger-text',
  info: 'text-raios-info-text',
} as const;

/* ─── Alert ────────────────────────────────────────────────────────────────── */

interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  dismissible?: boolean;
  onDismiss?: () => void;
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant = 'default', dismissible = false, onDismiss, children, ...props }, ref) => {
    const resolvedVariant = variant ?? 'default';
    const Icon = variantIconMap[resolvedVariant];
    const iconColor = variantIconColorMap[resolvedVariant];

    return (
      <div
        ref={ref}
        role="alert"
        className={cn(alertVariants({ variant }), className)}
        {...props}
      >
        <Icon className={cn('h-4 w-4 mt-0.5 shrink-0', iconColor)} />
        <div className="flex-1 min-w-0">{children}</div>
        {dismissible && (
          <button
            type="button"
            onClick={onDismiss}
            className="inline-flex shrink-0 items-center justify-center h-5 w-5 rounded-sm opacity-70 transition-opacity hover:opacity-100 cursor-pointer"
            aria-label="Dismiss"
          >
            <X className="h-3.5 w-3.5 text-raios-muted" />
          </button>
        )}
      </div>
    );
  }
);
Alert.displayName = 'Alert';

/* ─── AlertTitle ───────────────────────────────────────────────────────────── */

const AlertTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn('font-medium leading-snug text-raios-text', className)}
    {...props}
  />
));
AlertTitle.displayName = 'AlertTitle';

/* ─── AlertDescription ─────────────────────────────────────────────────────── */

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-raios-text-secondary mt-1', className)}
    {...props}
  />
));
AlertDescription.displayName = 'AlertDescription';

// eslint-disable-next-line react-refresh/only-export-components
export { Alert, AlertTitle, AlertDescription, alertVariants };
export type { AlertProps };
